import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { formatPrimaryShortcutLabel } from "@/components/ui/kbd"
import { useIsMacOs } from "@/hooks/use-is-macos"
import { isDialogPrimaryShortcutKey } from "@/components/ui/dialog-primary-shortcut"
import { focusFirstDialogField } from "@/components/ui/dialog-auto-focus"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-[2000] bg-[var(--scribane-black)]/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

function DialogPrimaryShortcut({ className }: { className?: string }) {
  const isMac = useIsMacOs()
  return (
    <span
      className={cn("hidden text-[10px] font-normal leading-none opacity-55 md:inline", className)}
      aria-hidden
    >
      {formatPrimaryShortcutLabel(isMac)}
    </span>
  )
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onOpenAutoFocus, onInteractOutside, onKeyDownCapture, ...props }, ref) => {
  const contentPadding = !className?.includes('p-0');
  const autoFocusCleanupRef = React.useRef<(() => void) | null>(null);
  React.useEffect(() => {
    return () => {
      autoFocusCleanupRef.current?.();
      autoFocusCleanupRef.current = null;
    };
  }, []);
  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      onKeyDownCapture={(e) => {
        onKeyDownCapture?.(e)
        if (e.defaultPrevented) {
          return
        }
        if (!isDialogPrimaryShortcutKey(e)) {
          return
        }
        const target = e.target as HTMLElement | null
        if (target?.closest?.("[data-dialog-primary-ignore-shortcut]")) {
          return
        }
        const contentEl = e.currentTarget as HTMLElement
        const primary = contentEl.querySelector<HTMLButtonElement>(
          "button[data-dialog-primary]:not([disabled])"
        )
        if (!primary) {
          return
        }
        e.preventDefault()
        e.stopPropagation()
        primary.click()
      }}
      onInteractOutside={(e) => {
        const target = e.target as Node;
        const mentionList = document.querySelector('[data-mention-list]');
        if (mentionList?.contains(target)) {
          e.preventDefault();
        }
        const createEntityDialog = document.querySelector('[data-create-entity-from-mention]');
        if (createEntityDialog?.contains(target)) {
          e.preventDefault();
        }
        const formattingBar = document.querySelector('[data-formatting-bar]');
        if (formattingBar?.contains(target)) {
          e.preventDefault();
        }
        const confirmDialog = document.querySelector('[data-confirm-dialog]');
        if (confirmDialog?.contains(target)) {
          e.preventDefault();
        }
        const entityRelevancePicker = document.querySelector('[data-entity-relevance-picker]');
        if (entityRelevancePicker?.contains(target)) {
          e.preventDefault();
        }
        onInteractOutside?.(e);
      }}
      className={cn(
        "fixed left-[50%] top-[50%] z-[2001] flex w-full max-w-3xl translate-x-[-50%] translate-y-[-50%] flex-col gap-4 border border-border bg-background text-foreground shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg max-h-[90vh] overflow-hidden max-[767px]:top-4 max-[767px]:translate-y-0 max-[767px]:max-h-[calc(100dvh-2rem)] [&:has([data-entity-detail-dialog-close])>[data-dialog-close]]:hidden",
        className
      )}
      tabIndex={-1}
      onOpenAutoFocus={(event) => {
        onOpenAutoFocus?.(event);
        if (event.defaultPrevented) {
          return;
        }

        const contentEl = event.currentTarget as HTMLElement | null;
        event.preventDefault();
        if (!contentEl) {
          return;
        }
        autoFocusCleanupRef.current?.();
        autoFocusCleanupRef.current = focusFirstDialogField(contentEl);
      }}
      {...props}
    >
      <div
        className={cn(
          "min-h-0 w-full min-w-0 flex-1 text-foreground",
          contentPadding
            ? "overflow-x-hidden overflow-y-auto py-6 px-6"
            : "flex flex-col overflow-hidden"
        )}
      >
        {children}
      </div>
      <DialogPrimitive.Close
        data-dialog-close=""
        className="absolute right-4 top-4 z-50 rounded-sm border-0 bg-transparent p-0 opacity-70 ring-offset-background transition-opacity hover:bg-accent hover:text-accent-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left mb-5",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight text-foreground",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogPrimaryShortcut,
}
